import React, { Component } from "react";
import { Link } from 'react-router-dom';
import Loader from "react-loaders";
import Select from "react-select";
import DataTable from "react-data-table-component";
import backlogo from '../../assets/images/svg/Back.svg';
import axiosInstance from "../configuration/Request";
import { AiOutlineDownload, AiOutlineSearch } from "react-icons/ai";
import { FiRotateCcw, FiChevronDown } from "react-icons/fi";
import GeneDetailPanel from "../ReusableComponent/GeneDetailPanel";
import RoundedPagination from "../ReusableComponent/RoundedPagination";
import { roundedSelectStyles, multiSelectStyles, dataTableCustomStyles, formatStat } from "../ReusableComponent/biomarkerTableStyles";

const CANCER_TYPE_MAP = {
  'NSCLC': 'Non-Small Cell Lung Cancer',
  'SCLC': 'Small Cell Lung Cancer',
};

const SURG_RAD_OPTIONS = [
  { value: 'AllSamples', label: 'All Patients' },
  { value: 'Surg', label: 'Surgery' },
  { value: 'Rad', label: 'Radiation' },
  { value: 'Both', label: 'Surgery and Radiation' },
];

const TABLE_COLUMNS = [
  { key: 'treatment', label: 'Treatment' },
  { key: 'gene_name', label: 'Gene Name' },
  { key: 'estimate', label: 'Estimate' },
  { key: 'std_error', label: 'Standard Error' },
  { key: 'statistic', label: 'Statistic' },
  { key: 'p_value', label: 'p-value' },
  { key: 'data_level', label: 'Data Level' },
  { key: 'cancer_type', label: 'Cancer Type' },
  { key: 'histological_subtype', label: 'Histological Subtype' },
  { key: 'surg_rad', label: 'Surgery / Radiation' },
  { key: 'category', label: 'Category' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'seer_version', label: 'SEER Version' },
];

const PAGE_SIZE = 10;
const GENE_COL_WIDTH = '170px';

const humanizeTreatmentLabel = (value) => (value || '')
  .replace(/\./g, ' ')
  .replace(/\+/g, ' + ')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeSurgRad = (value) => (value || '').toString().trim().toLowerCase();

const canonicalSurgRad = (row) => {
  const found = SURG_RAD_OPTIONS.find(o => o.value.toLowerCase() === normalizeSurgRad(row.surg_rad));
  return found ? found.value : 'AllSamples';
};

class BioMarkerTreatmentResultPage extends Component {
  constructor(props) {
    super(props);

    const locationState = props.location?.state || {};
    const { bioMarker, treatments } = locationState;
    const searchParams = new URLSearchParams(props.location?.search || '');
    const queryDrugs = searchParams.get('drugs');
    const initialDrugValues = queryDrugs
      ? queryDrugs.split(',').map(drug => drug.trim()).filter(Boolean)
      : treatments?.drugs || [];

    const initialDrugs = initialDrugValues.map(d => ({ value: d, label: humanizeTreatmentLabel(d) }));
    const initialSurgRadValue = searchParams.get('surgRad') || treatments?.surgRad;
    const initialSurgRad = SURG_RAD_OPTIONS.find(o => o.value === initialSurgRadValue) || SURG_RAD_OPTIONS[0];
    const initialCohort = searchParams.get('cohort') || treatments?.cohort || null;
    const cancerType = searchParams.get('cancerType') || bioMarker?.label || 'NSCLC';

    this.state = {
      treatmentOptions: [],
      cohortOptions: [],
      selectedTreatments: initialDrugs,
      selectedSurgRad: initialSurgRad,
      selectedCohort: null,
      initialSelectedCohort: initialCohort,
      genePanel: null,
      tableData: [],
      totalRows: 0,
      bioMarker: bioMarker || { label: cancerType },
      initialFilters: null,
      loader: false,
      dropdownLoader: true,
      searchTerm: '',
      significantOnly: false,
    };
  }

  componentDidMount() {
    this.fetchDropdowns();
  }

  fetchDropdowns = () => {
    this.setState({ dropdownLoader: true });
    axiosInstance
      .get(`${process.env.REACT_APP_BASE_URL}api/gene-dropdown/projection-dropdowns`)
      .then(res => {
        const treatment = res.data?.dropdowns?.treatment || {};
        const rawTreatments = treatment.treatment || [];
        const cohortOptions = treatment.cohort || [];

        const treatmentOptions = rawTreatments.map(t => ({
          value: t,
          label: humanizeTreatmentLabel(t),
        }));

        const defaultCohort = this.state.initialSelectedCohort ||
          (cohortOptions.includes('TCGA') ? 'TCGA' : (cohortOptions[0] || null));

        const initialFilters = {
          selectedTreatments: this.state.selectedTreatments,
          selectedSurgRad: this.state.selectedSurgRad,
          selectedCohort: defaultCohort,
        };

        this.setState({
          treatmentOptions,
          cohortOptions,
          selectedCohort: defaultCohort,
          initialFilters,
          dropdownLoader: false,
        }, this.fetchTableData);
      })
      .catch(err => {
        console.log(err);
        this.setState({ dropdownLoader: false });
      });
  };

  fetchTableData = () => {
    const { selectedTreatments, selectedSurgRad, selectedCohort, bioMarker } = this.state;

    if (!selectedCohort) return;

    const rawLabel = bioMarker?.label || 'NSCLC';
    const cancerType = CANCER_TYPE_MAP[rawLabel] || rawLabel;

    this.setState({ loader: true, tableData: [], totalRows: 0, genePanel: null });

    axiosInstance
      .post(`${process.env.REACT_APP_BASE_URL}api/treatment-projection/filter`, {
        data: {
          cancer_type: cancerType,
          treatment: selectedTreatments.map(t => t.value),
          SurgRad: selectedSurgRad?.value || 'AllSamples',
          cohort: selectedCohort,
          ...(this.state.significantOnly ? { sig: true } : {}),
        }
      })
      .then(res => {
        const data = res?.data?.data || [];
        this.setState({ tableData: data, totalRows: res?.data?.total || data.length });
      })
      .catch(err => {
        console.log(err);
        this.setState({ tableData: [], totalRows: 0 });
      })
      .finally(() => {
        this.setState({ loader: false });
      });
  };

  handleTreatmentChange = (selected) => {
    this.setState({ selectedTreatments: selected || [] });
  };

  removeTreatment = (value) => {
    this.setState(prev => ({
      selectedTreatments: prev.selectedTreatments.filter(t => t.value !== value),
    }));
  };

  handleSurgRadChange = (selected) => {
    this.setState({ selectedSurgRad: selected || SURG_RAD_OPTIONS[0] });
  };

  handleCohortChange = (selected) => {
    this.setState({ selectedCohort: selected?.value || null });
  };

  getRowKey = (row) => JSON.stringify([row.id, row.gene_name, row.treatment, normalizeSurgRad(row.surg_rad), row.data_level, row.histological_subtype, row.cancer_type, row.cohort, row.category, row.analysis_type, row.see_rversion || row.seer_version]);

  handleGeneClick = (row) => {
    const geneName = row.gene_name;
    const surgRad = canonicalSurgRad(row);
    const treatmentContext = { treatment: row.treatment, SurgRad: surgRad };

    const plots = [{
      id: this.getRowKey(row),
      histological_subtype: row.histological_subtype,
      dataLevel: row.data_level,
      category: row.category,
      cancer_type: row.cancer_type,
      estimate: row.estimate,
      p_value: row.p_value,
    }];

    this.setState({ genePanel: { geneName, plots, treatmentContext, rowKey: this.getRowKey(row) } });
  };

  closeGenePanel = () => this.setState({ genePanel: null });

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleSignificantOnlyChange = (e) => {
    this.setState({ significantOnly: e.target.checked }, this.fetchTableData);
  };

  onApply = () => {
    this.updateFilterUrl();
    this.fetchTableData();
  };

  updateFilterUrl = () => {
    const { selectedTreatments, selectedSurgRad, selectedCohort } = this.state;
    const searchParams = new URLSearchParams(this.props.location?.search || '');
    searchParams.set('drugs', selectedTreatments.map(treatment => treatment.value).join(','));
    searchParams.set('surgRad', selectedSurgRad?.value || 'AllSamples');
    if (selectedCohort) searchParams.set('cohort', selectedCohort);
    else searchParams.delete('cohort');
    this.props.history.replace({ pathname: this.props.location.pathname, search: `?${searchParams.toString()}` });
  };

  onReset = () => {
    const { initialFilters } = this.state;
    if (!initialFilters) return;
    this.setState({ ...initialFilters, searchTerm: '', significantOnly: false }, () => {
      this.updateFilterUrl();
      this.fetchTableData();
    });
  };

  handleDownloadCSV = () => {
    const tableData = this.getFilteredData();
    if (!tableData.length) return;

    const escape = (val) => {
      const s = val == null ? '' : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const header = TABLE_COLUMNS.map(c => escape(c.label)).join(',');
    const rows = tableData.map(row =>
      TABLE_COLUMNS.map(c => {
        if (c.key === 'surg_rad') return escape(row.surg_rad || 'AllSamples');
        if (c.key === 'seer_version') return escape(row.see_rversion || row.seer_version || '');
        return escape(row[c.key]);
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Biomarker_Treatment_Result_${this.getTableTitle().replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  isApplyDisabled = () => {
    const { selectedCohort } = this.state;
    return !selectedCohort;
  };

  getTableTitle = () => {
    const { selectedTreatments, selectedSurgRad, selectedCohort } = this.state;

    const parts = [];
    if (selectedSurgRad?.value && selectedSurgRad.value !== 'AllSamples') parts.push(selectedSurgRad.label);
    selectedTreatments.forEach(t => parts.push(t.label));

    let title = parts.join(' + ');
    if (selectedCohort) title += (title ? ' ' : '') + selectedCohort;

    return title || 'Select a treatment and click Apply to view results';
  };

  getDataLevelBadge = () => {
    const levels = [...new Set(this.state.tableData.map(r => r.data_level).filter(Boolean))];
    if (levels.length === 0) return null;
    if (levels.length <= 2) return levels.join(' / ');
    return 'Multiple data levels';
  };

  getFilteredData = () => {
    const { tableData, searchTerm } = this.state;
    const term = searchTerm.trim().toLowerCase();
    return tableData.filter(row => {
      if (term && !String(row.gene_name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  };

  getColumns = () => {
    return [
      { name: 'Treatment', selector: row => row.treatment, sortable: true, width: '220px' },
      { name: 'Gene Name', selector: row => row.gene_name, sortable: true, width: GENE_COL_WIDTH, cell: row => <div data-tag="allowRowEvents" className="bmt-gene-link">{row.gene_name}</div> },
      { name: 'Estimate', selector: row => Number(row.estimate), sortable: true, cell: row => formatStat(row.estimate, 4) },
      { name: 'Standard Error', selector: row => Number(row.std_error), sortable: true, cell: row => formatStat(row.std_error, 4) },
      { name: 'Statistic', selector: row => Number(row.statistic), sortable: true, cell: row => formatStat(row.statistic, 4) },
      { name: 'p-value', selector: row => Number(row.p_value), sortable: true, cell: row => formatStat(row.p_value, 4) },
      { name: 'Data Level', selector: row => row.data_level, sortable: true },
      { name: 'Cancer Type', selector: row => row.cancer_type, sortable: true },
      { name: 'Histological Subtype', selector: row => row.histological_subtype, sortable: true },
      { name: 'Surgery / Radiation', selector: row => row.surg_rad || 'AllSamples', sortable: true },
      { name: 'Category', selector: row => row.category, sortable: true },
      { name: 'Cohort', selector: row => row.cohort, sortable: true },
      { name: 'SEER Version', selector: row => row.see_rversion || row.seer_version || '', sortable: true },
    ];
  };

  render() {
    const {
      treatmentOptions, cohortOptions,
      selectedTreatments, selectedSurgRad,
      selectedCohort,
      genePanel,
      bioMarker, tableData, loader, dropdownLoader,
      searchTerm, significantOnly,
    } = this.state;

    const cancerTypeLabel = bioMarker?.label || 'NSCLC';
    const filteredData = this.getFilteredData();
    const distinctGeneCount = new Set(filteredData.map(r => r.gene_name)).size;
    const dataLevelBadge = this.getDataLevelBadge();

    return (
      <>
        <div className="bmt-page">
          <Link className="bmt-back-link" to={{ pathname: '/app/bioMarker' }}>
            <img src={backlogo} alt="" />
            <span>Back</span>
          </Link>

          <div className="bmt-layout">
            <div className="bmt-filter-col">
              <div className="bmt-filter-card">
                <div className="bmt-flow-badge">Biomarker Browser · By Treatment</div>
                <div className="bmt-filter-title">Filters</div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Cancer type</span>
                  <div className="bmt-static-field">{cancerTypeLabel}</div>
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Treatment</span>
                  <Select
                    isMulti
                    controlShouldRenderValue={false}
                    options={treatmentOptions}
                    value={selectedTreatments}
                    onChange={this.handleTreatmentChange}
                    placeholder={selectedTreatments.length > 0
                      ? `${selectedTreatments.length} treatment${selectedTreatments.length > 1 ? 's' : ''} selected`
                      : 'Select treatments...'}
                    isLoading={dropdownLoader}
                    styles={multiSelectStyles}
                  />
                  {selectedTreatments.length > 0 && (
                    <>
                      <div className="tp-chip-list">
                        {selectedTreatments.map(t => (
                          <span key={t.value} className="tp-chip">
                            <span>{t.label}</span>
                            <button type="button" className="tp-chip-remove" onClick={() => this.removeTreatment(t.value)}>×</button>
                          </span>
                        ))}
                      </div>
                      <button type="button" className="tp-clear-all" onClick={() => this.handleTreatmentChange([])}>Clear all</button>
                    </>
                  )}
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Surgery / radiation</span>
                  <Select
                    options={SURG_RAD_OPTIONS}
                    value={selectedSurgRad}
                    onChange={this.handleSurgRadChange}
                    styles={roundedSelectStyles}
                  />
                </div>

                <div className="bmt-filter-field">
                  <span className="bmt-filter-label">Cohort</span>
                  <Select
                    options={cohortOptions.map(c => ({ value: c, label: c }))}
                    value={selectedCohort ? { value: selectedCohort, label: selectedCohort } : null}
                    onChange={this.handleCohortChange}
                    placeholder="Select cohort..."
                    styles={roundedSelectStyles}
                  />
                </div>

                <div className="bmt-filter-actions">
                  <button type="button" className="bmt-btn-reset" onClick={this.onReset}>
                    <FiRotateCcw size={14} />
                    Reset
                  </button>
                  <button type="button" className="bmt-btn-apply" onClick={this.onApply} disabled={this.isApplyDisabled()}>
                    Apply filters
                  </button>
                </div>
              </div>
            </div>

            <div className="bmt-results-col">
              <div className="bmt-results-card prognosis-results-card">
                <div className="bmt-results-header">
                  <div>
                    <h2 className="bmt-results-title">
                      {selectedTreatments.length > 0
                        ? `${selectedTreatments.length} treatment${selectedTreatments.length > 1 ? 's' : ''} selected`
                        : this.getTableTitle()}
                    </h2>
                    <div className="bmt-results-subtitle">Biomarker associations</div>
                  </div>
                  <button
                    type="button"
                    className="bmt-btn-download"
                    onClick={this.handleDownloadCSV}
                    disabled={filteredData.length === 0}
                  >
                    <AiOutlineDownload size={16} />
                    Download CSV
                  </button>
                </div>

                <div className="bmt-chip-row">
                  <span className="bmt-chip">{cancerTypeLabel}</span>
                  {selectedCohort && <span className="bmt-chip">{selectedCohort}</span>}
                  {dataLevelBadge && <span className="bmt-chip">{dataLevelBadge}</span>}
                </div>

                {(loader || dropdownLoader) ? null : tableData.length === 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh', color: '#888', fontSize: '1rem', textAlign: 'center', padding: '0 2rem' }}>
                    No data found for the selected filters.
                  </div>
                ) : (
                  <>
                    <div className="bmt-toolbar">
                      <div className="tp-search-wrap">
                        <AiOutlineSearch className="tp-search-icon" size={14} />
                        <input
                          type="text"
                          placeholder="Search genes"
                          value={searchTerm}
                          onChange={this.handleSearchChange}
                        />
                      </div>
                      <label className="bmt-checkbox-label">
                        <input type="checkbox" checked={significantOnly} onChange={this.handleSignificantOnlyChange} />
                        Significant only
                      </label>
                      <span className="tp-badge" role="status">{filteredData.length} result{filteredData.length === 1 ? '' : 's'}</span>
                    </div>

                    <div className="bmt-table-wrap tp-data-table prognosis-table-shell gene-treatment-table">
                      <DataTable
                        columns={this.getColumns()}
                        data={filteredData}
                        onRowClicked={this.handleGeneClick}
                        customStyles={dataTableCustomStyles}
                        noDataComponent={<div style={{ padding: '24px', fontSize: '0.85rem', color: '#888' }}>No genes match the current search/filter.</div>}
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="58vh"
                        sortIcon={<FiChevronDown size={12} />}
                        conditionalRowStyles={[{
                          when: row => !!genePanel && this.getRowKey(row) === genePanel.rowKey,
                          style: { backgroundColor: '#eef0ff' },
                        }]}
                        pagination
                        paginationPerPage={PAGE_SIZE}
                        paginationComponentOptions={{ noRowsPerPage: true }}
                        paginationComponent={RoundedPagination}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {genePanel && (
          <GeneDetailPanel
            key={genePanel.rowKey}
            geneName={genePanel.geneName}
            plots={genePanel.plots}
            onClose={this.closeGenePanel}
            lambdaType="treatment"
            context={genePanel.treatmentContext}
          />
        )}

        {(loader || dropdownLoader) && (
          <div className="backdrop">
            <div className="loader">
              <Loader type="ball-pulse-rise" />
            </div>
          </div>
        )}
      </>
    );
  }
}

export default BioMarkerTreatmentResultPage;
